import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  listMemories,
  createMemory,
  updateMemory,
  deleteMemory,
  batchDeleteMemories,
  getMemoryCategories,
  type UserMemory,
} from "@/api/chat";

interface Category {
  id: string;
  label: string;
  icon: string;
}

const ICON_MAP: Record<string, string> = {
  heart: "ri-heart-3-fill",
  user: "ri-user-smile-fill",
  "bar-chart": "ri-bar-chart-fill",
  target: "ri-crosshair-fill",
  wallet: "ri-wallet-3-fill",
  more: "ri-more-fill",
};

const CONFIDENCE_LABEL: Record<number, string> = {
  1: "可能",
  2: "一般",
  3: "较确定",
  4: "很确定",
  5: "非常确定",
};

const SOURCE_LABEL: Record<string, string> = {
  auto_extracted: "自动提取",
  user_added: "手动添加",
  system_inferred: "系统推断",
};

export default function MemoryPage() {
  const navigate = useNavigate();
  const [memories, setMemories] = useState<UserMemory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addCategory, setAddCategory] = useState("preference");
  const [addContent, setAddContent] = useState("");
  const [total, setTotal] = useState(0);

  const loadMemories = useCallback(async (category?: string) => {
    setLoading(true);
    try {
      const r = await listMemories(category || undefined);
      const resp = r as any;
      setMemories(resp?.data || resp || []);
      setTotal(resp?.total || 0);
    } catch {
      setMemories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await getMemoryCategories();
      const resp = r as any;
      setCategories(resp?.data || resp || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadMemories(activeCategory);
  }, [activeCategory, loadMemories]);

  const handleCategoryFilter = (cat: string) => {
    setActiveCategory(activeCategory === cat ? "" : cat);
    setSelectedIds(new Set());
    setSelectMode(false);
  };

  const handleEdit = async (id: number) => {
    if (!editContent.trim()) return;
    try {
      await updateMemory(id, { content: editContent.trim() });
      setEditingId(null);
      loadMemories(activeCategory);
    } catch {
      // ignore
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMemory(id);
      loadMemories(activeCategory);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch {
      // ignore
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await batchDeleteMemories(Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectMode(false);
      loadMemories(activeCategory);
    } catch {
      // ignore
    }
  };

  const handleAdd = async () => {
    if (!addContent.trim()) return;
    try {
      await createMemory(addCategory, addContent.trim());
      setShowAdd(false);
      setAddContent("");
      setAddCategory("preference");
      loadMemories(activeCategory);
    } catch {
      // ignore
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer shrink-0">
            <i className="ri-arrow-left-s-line text-foreground-600 text-sm" />
          </button>
          <div>
            <h1 className="text-base font-bold text-foreground-950">AI 对我的了解</h1>
            <p className="text-[10px] text-foreground-400">共 {total} 条记忆</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!selectMode ? (
            <>
              <button onClick={() => setShowAdd(true)}
                className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center cursor-pointer">
                <i className="ri-add-line text-primary-500 text-sm" />
              </button>
              <button onClick={() => setSelectMode(true)}
                className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-checkbox-multiple-line text-foreground-600 text-sm" />
              </button>
            </>
          ) : (
            <>
              <span className="text-[10px] text-foreground-500 mr-1">已选 {selectedIds.size} 项</span>
              <button onClick={handleBatchDelete}
                className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-red-50 text-red-500 cursor-pointer">
                删除
              </button>
              <button onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}
                className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-background-100 text-foreground-600 cursor-pointer">
                取消
              </button>
            </>
          )}
        </div>
      </div>

      {/* Category filter */}
      <div className="px-4 pb-2 overflow-x-auto shrink-0">
        <div className="flex gap-1.5">
          {categories.map((cat) => (
            <button key={cat.id}
              onClick={() => handleCategoryFilter(cat.id)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-medium whitespace-nowrap cursor-pointer transition-colors ${
                activeCategory === cat.id
                  ? "bg-primary-500 text-white"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}>
              <i className={`${ICON_MAP[cat.icon] || "ri-price-tag-3-line"} text-xs`} />
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add memory form */}
      {showAdd && (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-primary-50 border border-primary-200 shrink-0">
          <select value={addCategory} onChange={(e) => setAddCategory(e.target.value)}
            className="w-full text-[11px] px-2 py-1.5 rounded-lg bg-white border border-background-200 mb-2 outline-none">
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
          <textarea value={addContent} onChange={(e) => setAddContent(e.target.value)}
            placeholder="输入 AI 应该记住的信息..."
            className="w-full text-[11px] px-2 py-1.5 rounded-lg bg-white border border-background-200 outline-none resize-none min-h-[60px] mb-2" />
          <div className="flex gap-1.5 justify-end">
            <button onClick={() => setShowAdd(false)}
              className="px-3 py-1.5 rounded-full text-[10px] font-medium bg-white text-foreground-600 border border-background-200 cursor-pointer">
              取消
            </button>
            <button onClick={handleAdd} disabled={!addContent.trim()}
              className="px-3 py-1.5 rounded-full text-[10px] font-medium bg-primary-500 text-white disabled:opacity-40 cursor-pointer">
              添加
            </button>
          </div>
        </div>
      )}

      {/* Memory list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line text-foreground-300 text-lg animate-spin" />
          </div>
        ) : memories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-brain-line text-foreground-300 text-2xl" />
            </div>
            <p className="text-xs text-foreground-400 mb-1">
              {activeCategory ? "该分类暂无记忆" : "AI 还没有关于你的记忆"}
            </p>
            <p className="text-[10px] text-foreground-300 text-center max-w-[240px] mb-3">
              AI 会自动从你的对话中学习，你也可以手动添加
            </p>
            <button onClick={() => setShowAdd(true)}
              className="px-3 py-1.5 rounded-full text-[10px] font-medium bg-primary-50 text-primary-600 border border-primary-200 cursor-pointer">
              <i className="ri-add-line text-xs align-middle" /> 添加记忆
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {memories.map((m) => (
              <div key={m.id}
                className={`p-3 rounded-xl border transition-colors ${
                  selectedIds.has(m.id) ? "border-primary-300 bg-primary-50" : "border-background-200 bg-white"
                }`}>
                {/* Row 1: select + category + confidence */}
                <div className="flex items-center gap-1.5 mb-1">
                  {selectMode && (
                    <div onClick={() => toggleSelect(m.id)}
                      className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer shrink-0 ${
                        selectedIds.has(m.id)
                          ? "bg-primary-500 border-primary-500 text-white"
                          : "border-foreground-300"
                      }`}>
                      {selectedIds.has(m.id) && <i className="ri-check-line text-[8px]" />}
                    </div>
                  )}
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">
                    <i className={`${ICON_MAP[categories.find(c => c.id === m.category)?.icon || "more"]} text-[8px] mr-0.5`} />
                    {categories.find(c => c.id === m.category)?.label || m.category}
                  </span>
                  <span className="text-[9px] text-foreground-400">
                    {CONFIDENCE_LABEL[m.confidence] || `置信度${m.confidence}`}
                  </span>
                  <span className="text-[9px] text-foreground-300 ml-auto">
                    {SOURCE_LABEL[m.source] || m.source}
                  </span>
                </div>

                {/* Row 2: content / edit */}
                {editingId === m.id ? (
                  <div className="flex gap-1.5">
                    <input value={editContent} onChange={(e) => setEditContent(e.target.value)}
                      className="flex-1 text-[11px] px-2 py-1 rounded-lg bg-background-50 border border-background-200 outline-none" />
                    <button onClick={() => handleEdit(m.id)}
                      className="px-2 py-1 rounded text-[10px] font-medium bg-primary-500 text-white cursor-pointer">
                      保存
                    </button>
                    <button onClick={() => setEditingId(null)}
                      className="px-2 py-1 rounded text-[10px] font-medium bg-background-100 text-foreground-600 cursor-pointer">
                      取消
                    </button>
                  </div>
                ) : (
                  <p className="text-[12px] text-foreground-800 leading-relaxed">{m.content}</p>
                )}

                {/* Row 3: action buttons (non-select mode) */}
                {!selectMode && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <button onClick={() => {
                      setEditingId(m.id);
                      setEditContent(m.content);
                    }}
                      className="text-[9px] text-primary-500 cursor-pointer hover:underline">
                      <i className="ri-edit-line text-[9px] mr-0.5" />编辑
                    </button>
                    <button onClick={() => handleDelete(m.id)}
                      className="text-[9px] text-red-400 cursor-pointer hover:underline">
                      <i className="ri-delete-bin-line text-[9px] mr-0.5" />删除
                    </button>
                    {m.sourceConversationId > 0 && (
                      <button onClick={() => navigate(`/ai-trader?conv=${m.sourceConversationId}`)}
                        className="text-[9px] text-foreground-400 cursor-pointer hover:underline">
                        <i className="ri-chat-1-line text-[9px] mr-0.5" />查看来源
                      </button>
                    )}
                    <span className="text-[8px] text-foreground-300 ml-auto">
                      {new Date(m.createdAt * 1000).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
